import { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { useWorkspace } from '@/stores/workspace.store';
import { useUi } from '@/stores/ui.store';
import { Q_MEDIA, M_ADD_MEDIA, M_DELETE_MEDIA } from '@/lib/queries';
import { mediaItems, mediaFolders } from '@/lib/wireframe-data';

type MediaScope = 'project' | 'org';
type MediaFilter = 'all' | 'image' | 'video' | 'audio' | 'gif';

const filterTabs: { id: MediaFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'image', label: 'Images' },
  { id: 'video', label: 'Videos' },
  { id: 'audio', label: 'Audio' },
  { id: 'gif', label: 'GIFs' },
];

const scopeTabs: MediaScope[] = ['project', 'org'];

/** Normalized shape the grid renders, sourced from live mediaAssets or demo data. */
interface MediaTile {
  id: string;
  name: string;
  type: string;
  size: string;
  uploaded: string;
  url?: string;
  live: boolean;
}

function typeIcon(type: string): string {
  if (type === 'image') return '▣';
  if (type === 'video') return '▶';
  if (type === 'audio') return '♪';
  return '▦';
}

function fmtBytes(n?: number): string {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} KB`;
  return `${n} B`;
}

function baseName(key: string): string {
  return key.split('/').pop() || key;
}

export default function MediaRoute() {
  const projectName = useWorkspace((s) => s.project_name) ?? 'Acme Corp';
  const projectId = useWorkspace((s) => s.project_id);
  const toast = useUi((s) => s.toast);

  const [mediaQ, refetch] = useQuery({ query: Q_MEDIA, variables: { limit: 200 } });
  const [, addMedia] = useMutation(M_ADD_MEDIA);
  const [, deleteMedia] = useMutation(M_DELETE_MEDIA);

  const [mediaScope, setMediaScope] = useState<MediaScope>('project');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');
  const [search, setSearch] = useState('');

  // Add-by-URL panel (no presigned-multipart endpoint yet — DEV-1 backlog).
  const [showAdd, setShowAdd] = useState(false);
  const [url, setUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);

  // Live assets when the API has any; otherwise fall back to demo data so the
  // library still renders populated before the backend is wired.
  const tiles: MediaTile[] = useMemo(() => {
    const live = mediaQ.data?.mediaAssets ?? [];
    if (live.length > 0) {
      return live.map((m: any): MediaTile => ({
        id: String(m.id),
        name: m.altText || baseName(m.storageKey || ''),
        type: m.kind || 'image',
        size: fmtBytes(m.sizeBytes),
        uploaded: m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '—',
        url: m.storageKey,
        live: true,
      }));
    }
    return mediaItems.map((m): MediaTile => ({
      id: String(m.id), name: m.name, type: m.type, size: m.size, uploaded: m.uploaded, live: false,
    }));
  }, [mediaQ.data]);

  const visible = tiles
    .filter((m) => mediaFilter === 'all' || m.type === mediaFilter)
    .filter((m) => !search || m.name.toLowerCase().includes(search.toLowerCase()));

  const submitAdd = async () => {
    if (!url.trim()) { toast('Paste a media URL first', 'warning'); return; }
    if (!projectId) { toast('Pick a project first', 'error'); return; }
    setBusy(true);
    const r = await addMedia({
      input: {
        projectId,
        url: url.trim(),
        altText: altText.trim() || undefined,
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
      },
    });
    setBusy(false);
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Media added');
    setUrl(''); setAltText(''); setTags(''); setShowAdd(false);
    refetch({ requestPolicy: 'network-only' });
  };

  const onDelete = async (tile: MediaTile) => {
    if (!tile.live) { toast('Demo asset — connect the API to manage real media', 'warning'); return; }
    const r = await deleteMedia({ id: tile.id });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Media deleted');
    refetch({ requestPolicy: 'network-only' });
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-stone-900 mb-5">Media library</h1>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-60 flex-shrink-0 space-y-4">
          <div className="bg-white border border-stone-200 rounded-xl p-3">
            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Scope</label>
            <div className="flex bg-stone-100 rounded-lg p-1">
              {scopeTabs.map((s) => (
                <button key={s} onClick={() => setMediaScope(s)}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${mediaScope === s ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}>
                  {s === 'project' ? 'This project' : 'Organization'}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-stone-400 mt-2">{mediaScope === 'project' ? `Only visible to ${projectName}` : 'Shared across all projects'}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Folders</label>
              <button className="text-xs text-violet-600">+ New</button>
            </div>
            <div className="space-y-1">
              {mediaFolders.map((f) => (
                <button key={f.id} onClick={() => setSelectedFolder(f.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${selectedFolder === f.id ? 'bg-violet-50 text-violet-700' : 'text-stone-600 hover:bg-stone-50'}`}>
                  <span className="flex items-center gap-2"><span>{f.icon}</span>{f.name}</span>
                  <span className="text-xs text-stone-400">{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-stone-700">Storage</span>
              <span className="text-xs text-stone-500">2.4 / 10 GB</span>
            </div>
            <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full" style={{ width: '24%' }} />
            </div>
            <button className="text-xs text-violet-600 hover:text-violet-700 mt-2 font-medium">Upgrade storage</button>
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <input value={search} onChange={(e) => setSearch(e.target.value)} type="text" placeholder="Search media..."
                className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">⌕</span>
            </div>
            <div className="flex bg-white border border-stone-200 rounded-lg p-1">
              {filterTabs.map((f) => (
                <button key={f.id} onClick={() => setMediaFilter(f.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mediaFilter === f.id ? 'bg-stone-100 text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowAdd((v) => !v)}
              className="px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium whitespace-nowrap">
              ⬆ Add media
            </button>
          </div>

          {showAdd && (
            <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-stone-800">Add media by URL</p>
              <p className="text-[11px] text-stone-400">Direct file/multipart upload lands once the presign endpoint ships (DEV-1). For now, paste a hosted asset URL.</p>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/image.jpg"
                className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={altText} onChange={(e) => setAltText(e.target.value)} placeholder="Alt text (optional)"
                  className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated"
                  className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg">Cancel</button>
                <button disabled={busy} onClick={submitAdd} className="px-4 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium disabled:opacity-50">{busy ? 'Adding…' : 'Add media'}</button>
              </div>
            </div>
          )}

          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {visible.map((item) => (
              <div key={item.id} className="group bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-300">
                <div className="aspect-square bg-gradient-to-br from-violet-200 to-violet-400 relative flex items-center justify-center text-white overflow-hidden">
                  {item.url && item.type === 'image'
                    ? <img src={item.url} alt={item.name} loading="lazy" className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                    : <span className="text-3xl">{typeIcon(item.type)}</span>}
                  <div className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded uppercase">{item.type}</div>
                  <button onClick={() => onDelete(item)} aria-label="Delete media"
                    className="absolute top-2 right-2 w-6 h-6 rounded bg-black/40 text-white text-xs opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity">✕</button>
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-900 truncate">{item.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-stone-500">{item.size}</span>
                    <span className="text-xs text-stone-400">{item.uploaded}</span>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => setShowAdd(true)}
              className="aspect-square border-2 border-dashed border-stone-300 rounded-xl flex flex-col items-center justify-center text-stone-400 hover:border-stone-400 hover:text-stone-600 transition-colors">
              <span className="text-3xl mb-2">+</span>
              <span className="text-sm font-medium">Add media</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
